import React, {
    useEffect,
    useState,
    forwardRef,
    useImperativeHandle,
    ForwardedRef,
} from "react";
import { Card, Checkbox, DateTimePicker, Input } from "../../../../../shared/custom-ui";

interface CTScanFormProps {
    defaultValues?: Record<string, any>;
}

const CTScanForm = forwardRef((props: CTScanFormProps, ref: ForwardedRef<any>) => {
    const { defaultValues = {} } = props;
    const [ctScanObj, setCtScanObj] = useState<Record<string, any>>({});
    const [isPrefilled, setIsPrefilled] = useState(false);

    // ✅ Apply default values only once (on first mount)
    useEffect(() => {
        if (!isPrefilled && Object.keys(defaultValues).length > 0) {
            setCtScanObj(prev => ({ ...prev, ...defaultValues }));
            setIsPrefilled(true);
        }
    }, [defaultValues, isPrefilled]);

    useImperativeHandle(ref, () => ({
        getData: () => ctScanObj,
    }));

    const handleCheckboxChange = (key: string) => {
        setCtScanObj((prev) => {
            if (prev[key]) {
                const { [key]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [key]: true };
        });
    };

    const handleInputChange = (key: string, value: string | null) => {
        setCtScanObj((prev) => {
            if (value) {
                return { ...prev, [key]: value };
            } else {
                const { [key]: _, ...rest } = prev;
                return rest;
            }
        });
    };

    return (
        <Card title="CT Scan" padding="small" variant="default">
            <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Checkbox
                        label="Contrast as determined by radiologist"
                        checked={ctScanObj.contrast_radiologist || false}
                        onChange={() => handleCheckboxChange("contrast_radiologist")}
                    />
                    <Checkbox
                        label="Without contrast"
                        checked={ctScanObj.without_contrast || false}
                        onChange={() => handleCheckboxChange("without_contrast")}
                    />
                    <Checkbox
                        label="With contrast"
                        checked={ctScanObj.with_contrast || false}
                        onChange={() => handleCheckboxChange("with_contrast")}
                    />
                    <Checkbox
                        label="Without & with contrast"
                        checked={ctScanObj.without_with_contrast || false}
                        onChange={() => handleCheckboxChange("without_with_contrast")}
                    />
                    <Checkbox
                        label="Draw Creatinine labs if medically indicated for contrast delivery"
                        checked={ctScanObj.creatinine_labs || false}
                        onChange={() => handleCheckboxChange("creatinine_labs")}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 mt-4 gap-4">
                    <Input
                        label="Please provide Creatinine level"
                        placeholder="Level"
                        value={ctScanObj.creatinine_level || ""}
                        onChange={(e) => handleInputChange("creatinine_level", e.target.value)}
                    />
                    <DateTimePicker
                        label="Date"
                        pickerType="date"
                        value={ctScanObj.date || ""}
                        onChange={(newDate) => handleInputChange("date", newDate)}
                    />
                </div>

                <h3 className="text-lg font-semibold mt-4">Abdomen/Pelvis</h3>
                <div className="grid grid-cols-1 mt-3 md:grid-cols-2 gap-4">
                    <div className="grid grid-cols-2 gap-4 ">
                        <Checkbox
                            label="Abdomen with pelvis"
                            checked={ctScanObj.abdomen_with_pelvis || false}
                            onChange={() => handleCheckboxChange("abdomen_with_pelvis")}
                        />
                        <Checkbox
                            label="Abdomen only"
                            checked={ctScanObj.abdomen_only || false}
                            onChange={() => handleCheckboxChange("abdomen_only")}
                        />
                    </div>
                    <Checkbox
                        label="Renal colic-Stone Protocol (CT abdomen/pelvis w/o contrast)"
                        checked={ctScanObj.renal_colic || false}
                        onChange={() => handleCheckboxChange("renal_colic")}
                    />
                    <Checkbox
                        label="Enterography (CT abdomen/pelvis)"
                        checked={ctScanObj.enterography || false}
                        onChange={() => handleCheckboxChange("enterography")}
                    />
                    <Checkbox
                        label="Urogram (CT abdomen/pelvis w/o/w contrast)"
                        checked={ctScanObj.urogram || false}
                        onChange={() => handleCheckboxChange("urogram")}
                    />
                    <Checkbox
                        label="Pelvis only"
                        checked={ctScanObj.pelvis_only || false}
                        onChange={() => handleCheckboxChange("pelvis_only")}
                    />
                </div>

                <h3 className="text-lg font-semibold mt-4">Chest</h3>
                <div className="grid grid-cols-1 mt-3 gap-4">
                    <div className="grid grid-cols-1 gap-4 ">
                        <Checkbox
                            label="Chest"
                            checked={ctScanObj.chest || false}
                            onChange={() => handleCheckboxChange("chest")}
                        />
                        <div className="grid grid-cols-2 mx-4 gap-6">
                            <Checkbox
                                label="Pulmonary embolus"
                                checked={ctScanObj.pulmonary_embolus || false}
                                onChange={() => handleCheckboxChange("pulmonary_embolus")}
                            />
                            <Checkbox
                                label="High-Res"
                                checked={ctScanObj.high_res || false}
                                onChange={() => handleCheckboxChange("high_res")}
                            />
                            <Checkbox
                                label="Nav bronch"
                                checked={ctScanObj.nav_bronch || false}
                                onChange={() => handleCheckboxChange("nav_bronch")}
                            />
                            <Checkbox
                                label="3D recon for"
                                checked={ctScanObj.recon_for || false}
                                onChange={() => handleCheckboxChange("recon_for")}
                            />
                            <Checkbox
                                label="Lung nodule"
                                checked={ctScanObj.lung_nodule || false}
                                onChange={() => handleCheckboxChange("lung_nodule")}
                            />
                            <Checkbox
                                label="Lung screening"
                                checked={ctScanObj.lung_screening || false}
                                onChange={() => handleCheckboxChange("lung_screening")}
                            />
                            <Checkbox
                                label="Annual"
                                checked={ctScanObj.annual || false}
                                onChange={() => handleCheckboxChange("annual")}
                            />
                            <Checkbox
                                label="Short-term follow-up"
                                checked={ctScanObj.short_term_follow_up || false}
                                onChange={() => handleCheckboxChange("short_term_follow_up")}
                            />
                        </div>
                    </div>
                    <Checkbox
                        label="Coronary calcium scoring (CCS) (asymptomatic risk stratification)"
                        checked={ctScanObj.ccs || false}
                        onChange={() => handleCheckboxChange("ccs")}
                    />
                    <Checkbox
                        label="Pectus deformity"
                        checked={ctScanObj.pectus_deformity || false}
                        onChange={() => handleCheckboxChange("pectus_deformity")}
                    />
                </div>

                <h3 className="text-lg font-semibold mt-4">Head/Neck</h3>
                <div className="grid grid-cols-1 mt-3 md:grid-cols-2 gap-4">
                    <Checkbox
                        label="Head/Brain"
                        checked={ctScanObj.head_brain || false}
                        onChange={() => handleCheckboxChange("head_brain")}
                    />
                    <Checkbox
                        label="Facial bones"
                        checked={ctScanObj.facial_bones || false}
                        onChange={() => handleCheckboxChange("facial_bones")}
                    />
                    <Checkbox
                        label="Orbits"
                        checked={ctScanObj.orbits || false}
                        onChange={() => handleCheckboxChange("orbits")}
                    />
                    <Checkbox
                        label="Temporal bones"
                        checked={ctScanObj.temporal_bones || false}
                        onChange={() => handleCheckboxChange("temporal_bones")}
                    />
                    <Checkbox
                        label="Sinus"
                        checked={ctScanObj.sinus || false}
                        onChange={() => handleCheckboxChange("sinus")}
                    />
                    <div className="grid grid-cols-2">
                        <Checkbox
                            label="routine"
                            checked={ctScanObj.routine || false}
                            onChange={() => handleCheckboxChange("routine")}
                        />
                        <Checkbox
                            label="surgical planning"
                            checked={ctScanObj.surgical_planning || false}
                            onChange={() => handleCheckboxChange("surgical_planning")}
                        />
                    </div>
                    <Checkbox
                        label="Neck-soft tissue"
                        checked={ctScanObj.neck_soft_tissue || false}
                        onChange={() => handleCheckboxChange("neck_soft_tissue")}
                    />
                </div>

                <h3 className="text-lg font-semibold mt-4">Angiography</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <div className="grid md:grid-cols-2 mt-2 md:mt-0 grid-cols-1 gap-y-3 md:gap-y-0">
                        <Checkbox
                            label="Angiography (specify area)"
                            checked={ctScanObj.angiography || false}
                            onChange={() => handleCheckboxChange("angiography")}
                        />
                        <Input
                            label="Specify area"
                            value={ctScanObj.angiography_note || ""}
                            onChange={(e) => handleInputChange("angiography_note", e.target.value)}
                        />
                    </div>
                </div>

                <h3 className="text-lg font-semibold mt-4">Orthopedic</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div className="grid grid-cols-2">
                        <Checkbox
                            label="Extremity (specify area)"
                            checked={ctScanObj.extremity || false}
                            onChange={() => handleCheckboxChange("extremity")}
                        />
                        <Input
                            label="Specify area"
                            value={ctScanObj.orthopedic_note || ""}
                            onChange={(e) => handleInputChange("orthopedic_note", e.target.value)}
                        />
                    </div>
                    <div className="grid grid-cols-3 md:items-end">
                        <Checkbox
                            label="L"
                            checked={ctScanObj.L || false}
                            onChange={() => handleCheckboxChange("orthopedic_L")}
                        />
                        <Checkbox
                            label="R"
                            checked={ctScanObj.R || false}
                            onChange={() => handleCheckboxChange("orthopedic_R")}
                        />
                        <Checkbox
                            label="3D recon"
                            checked={ctScanObj.recon || false}
                            onChange={() => handleCheckboxChange("orthopedic_recon")}
                        />
                    </div>
                    <div className="grid grid-cols-2">
                        <Checkbox
                            label="Arthrogram (specify joint)"
                            checked={ctScanObj.arthrogram || false}
                            onChange={() => handleCheckboxChange("arthrogram")}
                        />
                    </div>
                </div>

                <h3 className="text-lg font-semibold mt-4">Spine</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <Checkbox
                        label="Cervical"
                        checked={ctScanObj.cervical || false}
                        onChange={() => handleCheckboxChange("cervical")}
                    />
                    <Checkbox
                        label="Thoracic"
                        checked={ctScanObj.thoracic || false}
                        onChange={() => handleCheckboxChange("thoracic")}
                    />
                    <Checkbox
                        label="Lumbosacral"
                        checked={ctScanObj.lumbosacral || false}
                        onChange={() => handleCheckboxChange("lumbosacral")}
                    />
                </div>

                <h3 className="text-lg font-semibold mt-4">Other CT</h3>
                <div className="grid grid-cols-2 gap-4 mt-2">
                    <Checkbox
                        label="Other CT"
                        checked={ctScanObj.other_ct || false}
                        onChange={() => handleCheckboxChange("other_ct")}
                    />
                    <Input
                        label="Specify other CT"
                        placeholder="Specify other CT"
                        value={ctScanObj.specify_other_ct || ""}
                        onChange={(e) => handleInputChange("specify_other_ct", e.target.value)}
                    />
                </div>
            </div>
        </Card>
    );
});

export default CTScanForm;
